import { isNil } from 'lodash';
import * as yup from 'yup';
import { logger } from '../../../../../../util/logger';
import { SystemUnit } from '../../../../shared/model/flow.types';
import { convertUnits } from '../../../../shared/util/math';
import { BeaconWeldingData } from '../../model/beacon-welding-data';

interface ConvertEquipmentConfigUomParams {
  system: SystemUnit;
  welder: BeaconWeldingData.FlowConfig['welder'];
  standardize: boolean;
}

const convertEquipmentConfigUom = (
  params: ConvertEquipmentConfigUomParams
): BeaconWeldingData.FlowConfig['welder']['customFields'] => {
  const { system, welder, standardize } = params;

  if (welder.model === BeaconWeldingData.Welder.MILLER_DELTAWELD) {
    return convertWeldConfigCustomFieldsUom({
      system,
      customFields: welder.customFields,
      configMeta: BeaconWeldingData.MillerDeltaWeldConfigCustomFieldUom,
      standardize,
    });
  }

  logger.warn('Custom fields conversion not implemented for welder model', {
    welder,
  });
};

interface ConvertWeldConfigCustomFieldsUomParams {
  system: SystemUnit;
  customFields: BeaconWeldingData.FlowConfig['welder']['customFields'];
  configMeta: BeaconWeldingData.EquipmentConfigCustomFieldMeta;
  standardize: boolean;
}

const convertWeldConfigCustomFieldsUom = (
  params: ConvertWeldConfigCustomFieldsUomParams
): BeaconWeldingData.FlowConfig['welder']['customFields'] => {
  const { system, customFields, configMeta, standardize } = params;

  if (customFields === undefined) {
    return;
  }

  return Object.entries(customFields).reduce(
    (result, [k, v]) => {
      const uom = configMeta[k]?.uom;
      if (!uom || !uom.system) {
        result[k] = v;
        return result;
      }

      const [source, target] = standardize
        ? [uom.system[system], uom.target]
        : [uom.target, uom.system[system]];

      result[k] = convertUnits(v as number, source, target, {
        precision: uom.precision,
      });

      return result;
    },
    {} as Record<string, unknown>
  );
};

interface BuildConfigCustomFieldsValidationSchemaParams {
  system: SystemUnit;
  welder: BeaconWeldingData.FlowConfig['welder'];
}

const buildConfigCustomFieldsValidationSchema = (
  params: BuildConfigCustomFieldsValidationSchemaParams
): yup.ObjectSchema<any> | undefined => {
  const { system, welder } = params;

  if (welder.model === BeaconWeldingData.Welder.MILLER_DELTAWELD) {
    const uomMap = mapFieldsToUomRequirements(
      system,
      BeaconWeldingData.MillerDeltaWeldConfigCustomFieldUom
    );

    return yup.object().shape({
      customFields: yup.object().shape({
        sequenceRunInMode: yup.number().required().oneOf([0, 1, 2]),
        sequenceRunInWfs: yup.number().when('sequenceRunInMode', {
          is: 2,
          then: (schema) => schema.min(0).max(100).required(),
        }),
        sequenceStartMode: yup.number().required().oneOf([0, 1, 2]),
        sequenceStartWfs: yup.number().when('sequenceStartMode', {
          is: 2,
          then: (schema) => schema.min(50).max(250).required(),
        }),
        sequenceStartVoltage: yup.number().when('sequenceStartMode', {
          is: 2,
          then: (schema) => schema.min(50).max(150).required(),
        }),
        sequenceStartArcLength: yup.number().when('sequenceStartMode', {
          is: 2,
          then: (schema) => schema.min(50).max(150).required(),
        }),
        sequenceStartDwellTime: yup.number().when('sequenceStartMode', {
          is: 2,
          then: (schema) => schema.min(0.1).max(0.5).required(),
        }),
        sequenceStartRampTime: yup.number().when('sequenceStartMode', {
          is: 2,
          then: (schema) => schema.min(0.1).max(5).required(),
        }),
        sequenceBurnBackTime: yup.number().min(0).max(100).required(),
        sequenceRetractEnable: yup.boolean().required(),
        sequenceRetractDistance: yup
          .number()
          .min(uomMap.sequenceRetractDistance.min)
          .max(uomMap.sequenceRetractDistance.max)
          .required(),
      }),
    });
  }
};

interface UomRequirement {
  min: number;
  max: number;
}

const mapFieldsToUomRequirements = (
  system: SystemUnit,
  configCustomFieldMeta: BeaconWeldingData.EquipmentConfigCustomFieldMeta
): Record<string, UomRequirement> => {
  return Object.entries(configCustomFieldMeta).reduce(
    (result, [k, meta]) => {
      const { uom } = meta;

      if (isNil(meta.min) || isNil(meta.max)) {
        return result;
      }

      if (!uom.system || !uom.system[system]) {
        result[k].min = meta.min;
        result[k].max = meta.max;
        return result;
      }

      const min = convertUnits(meta.min, uom.target, uom.system[system], {
        precision: uom.precision,
      });

      const max = convertUnits(meta.max, uom.target, uom.system[system], {
        precision: uom.precision,
      });

      result[k] = {
        min,
        max,
      };

      return result;
    },
    {} as Record<string, UomRequirement>
  );
};

export {
  buildConfigCustomFieldsValidationSchema,
  convertEquipmentConfigUom,
  mapFieldsToUomRequirements,
};
